import { toast } from 'react-toastify';
import { resolveNotificationMessage } from '../utils/notificationMessages';
import {
  listMyNotifications,
  markNotificationRead as apiMarkRead,
  markAllNotificationsRead as apiMarkAllRead,
} from '../api/notifications';

// Polling-based notification feed (no socket). The server persists every
// notification, so we poll the persisted feed on an interval + on window focus.
// This mirrors how the mobile apps consume notifications. Socket.io is still
// used elsewhere (WhatsApp), but no longer for notifications.
const POLL_INTERVAL_MS = 45000;

class NotificationService {
  constructor() {
    this.isConnected = false;          // "last poll succeeded" — drives the bell's status dot
    this.listeners = new Map();
    this.notifications = [];
    this.maxNotifications = 100;
    this.seenIds = new Set();           // ids already shown — used to toast only newly-arrived items
    this.clearedBefore = 0;             // local "clear all" guard (epoch ms); poll won't resurrect older items
    this._pollTimer = null;
    this._onFocus = null;
    this._onVisible = null;
  }

  // Customer portal entry point (customerId kept for signature compatibility;
  // the API picks the endpoint by who is logged in).
  connect() {
    this._startPolling();
  }

  // Admin/staff layout entry point.
  connectAsAdmin() {
    this._startPolling();
  }

  _startPolling() {
    this._stopPolling();
    this.loadPersisted(true);
    this._pollTimer = setInterval(() => this.loadPersisted(false), POLL_INTERVAL_MS);

    this._onFocus = () => this.loadPersisted(false);
    this._onVisible = () => {
      if (document.visibilityState === 'visible') this.loadPersisted(false);
    };
    window.addEventListener('focus', this._onFocus);
    document.addEventListener('visibilitychange', this._onVisible);
  }

  _stopPolling() {
    if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; }
    if (this._onFocus) { window.removeEventListener('focus', this._onFocus); this._onFocus = null; }
    if (this._onVisible) { document.removeEventListener('visibilitychange', this._onVisible); this._onVisible = null; }
  }

  // Fetch the persisted feed. On the first load we seed `seenIds` silently;
  // on later polls, anything new gets a toast (preserving the live-popup UX).
  async loadPersisted(initial = false) {
    try {
      const res = await listMyNotifications({ limit: 100 });
      if (!res?.success || !Array.isArray(res.data)) {
        this._setConnected(false);
        return;
      }

      const incoming = res.data
        .filter((n) => new Date(n.timestamp || n.receivedAt || 0).getTime() > this.clearedBefore)
        .sort((a, b) => new Date(b.timestamp || b.receivedAt || 0) - new Date(a.timestamp || a.receivedAt || 0))
        .slice(0, this.maxNotifications);

      if (!initial) {
        // Toast newly-arrived items, oldest first so the newest ends up on top.
        const fresh = incoming.filter((n) => n.id && !this.seenIds.has(n.id));
        fresh.slice().reverse().forEach((n) => this._toastNotification(n));
      }
      incoming.forEach((n) => { if (n.id) this.seenIds.add(n.id); });

      this.notifications = incoming;
      this._setConnected(true);
      this.notifyListeners('notifications-loaded', { notifications: this.notifications });
    } catch (e) {
      console.error('❌ Failed to load notifications:', e.message);
      this._setConnected(false);
    }
  }

  _setConnected(connected) {
    if (this.isConnected !== connected) {
      this.isConnected = connected;
      this.notifyListeners('connection', { connected });
    }
  }

  // Toast a freshly-arrived notification, styled by type/subType.
  _toastNotification(notification) {
    const msg = resolveNotificationMessage(notification);
    const options = {
      position: 'top-right', autoClose: 6000,
      hideProgressBar: false, closeOnClick: true, pauseOnHover: true, draggable: true,
    };

    if (notification.type === 'wallet_update') {
      if (notification.subType === 'approved') toast.success(`✅ ${msg}`, options);
      else if (notification.subType === 'rejected') toast.error(`❌ ${msg}`, options);
      else toast.info(`💰 ${msg}`, options);
    } else if (notification.type === 'wallet_topup_request') {
      toast.info(`💰 ${msg}`, options);
    } else if (notification.type === 'package_update') {
      if (notification.station?.finalStation || notification.status?.toLowerCase() === 'delivered') {
        toast.success(`📦 ${msg}`, options);
      } else {
        toast.info(`🚛 ${msg}`, options);
      }
    } else {
      toast.info(msg, options);
    }
  }

  // Get all notifications
  getNotifications() {
    return [...this.notifications];
  }

  // Mark notification as read (optimistic + persist)
  markAsRead(notificationId) {
    const notification = this.notifications.find((n) => n.id === notificationId);
    if (notification && !notification.isRead) {
      notification.isRead = true;
      this.notifyListeners('notification-read', { id: notificationId });
      apiMarkRead(notificationId).catch((e) => console.error('markAsRead failed:', e));
    }
  }

  // Mark all notifications as read (optimistic + persist)
  markAllAsRead() {
    this.notifications.forEach((n) => { n.isRead = true; });
    this.notifyListeners('all-notifications-read', {});
    apiMarkAllRead().catch((e) => console.error('markAllAsRead failed:', e));
  }

  // Get unread count
  getUnreadCount() {
    return this.notifications.filter((n) => !n.isRead).length;
  }

  // Clear all notifications locally. There is no server-side delete, so we keep
  // a timestamp guard to stop polling from resurrecting what was just cleared.
  clearNotifications() {
    this.clearedBefore = Date.now();
    this.notifications = [];
    this.notifyListeners('notifications-cleared', {});
  }

  // Subscribe to events
  subscribe(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);

    return () => {
      const eventListeners = this.listeners.get(event);
      if (eventListeners) {
        eventListeners.delete(callback);
      }
    };
  }

  // Notify listeners
  notifyListeners(event, data) {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => callback(data));
    }
  }

  // Stop polling (called on layout unmount)
  disconnect() {
    this._stopPolling();
    this._setConnected(false);
  }

  // Get connection status (last poll succeeded)
  isSocketConnected() {
    return this.isConnected;
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
